import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Save, X, Plus, Loader2, Trash2, Receipt, Pill, AlertOctagon, StickyNote, Undo2, FlaskConical, CheckCircle, Clock, Download, MessageCircle } from 'lucide-react';

import api, { authFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: '', phone: '', email: '', date_of_birth: '', gender: '', address: '',
  allergies: [], notes: '', category: 'cash', g6pd_status: '', sickling_status: '',
  medical_history: '', medical_conditions: '', current_medications: '',
  social_history: '', environmental_factors: '', pregnancy_status: '',
};

const EVENT_ICON = { sale: Receipt, return: Undo2, prescription: Pill, drug_interaction: AlertOctagon, note: StickyNote, counseling_slip: MessageCircle };

export const PatientRecordTab = ({ legacyPatientId, seed, onCreated }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isPharmacist = ['super_admin', 'admin', 'pharmacist'].includes(user?.role);
  const [record, setRecord] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [aiNotes, setAiNotes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [allergyInput, setAllergyInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [loading, setLoading] = useState(!!legacyPatientId);
  const [reviewingId, setReviewingId] = useState(null);

  const loadAiNotes = async (id) => {
    if (!isPharmacist) return;
    try {
      const r = await api.get(`/duti/clinical-notes/patient/${id}`);
      setAiNotes(r.data.notes || []);
    } catch { /* silent — not critical */ }
  };

  const load = async (id) => {
    setLoading(true);
    try {
      const [p, tl] = await Promise.all([api.get(`/patients/${id}`), api.get(`/patients/${id}/timeline`)]);
      setRecord(p.data);
      setTimeline(tl.data);
      await loadAiNotes(id);
    } catch (e) { toast.error('Could not load patient record'); }
    finally { setLoading(false); }
  };

  const markReviewed = async (noteId) => {
    setReviewingId(noteId);
    try {
      await api.patch(`/duti/clinical-notes/${noteId}/review`, { note: '' });
      setAiNotes((prev) => prev.map((n) => n.note_id === noteId ? { ...n, status: 'reviewed', reviewed_by_name: user?.name || 'You', reviewed_at: new Date().toISOString() } : n));
      toast.success('Marked as reviewed');
    } catch { toast.error('Could not update note'); }
    finally { setReviewingId(null); }
  };

  const downloadPdf = async (noteId) => {
    try {
      const resp = await authFetch(`${API_URL}/api/duti/clinical-notes/${noteId}/pdf`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Could not generate PDF');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `clinical_note_${noteId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF generation failed'); }
  };

  const shareWhatsApp = (note) => {
    const phone = (record?.phone || '').replace(/\D/g, '');
    const msg = `Clinical Note — ${note.patient_name || 'Patient'}\n\nQ: ${note.question}\n\nAI Answer: ${note.answer.slice(0, 600)}${note.answer.length > 600 ? '…' : ''}\n\nDrugs: ${(note.drugs || []).join(', ') || 'N/A'}\nDate: ${note.created_at ? new Date(note.created_at).toLocaleDateString() : ''}\nBy: ${note.created_by_name}`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (legacyPatientId) load(legacyPatientId); else { setRecord(null); setTimeline(null); }
  }, [legacyPatientId]);

  const startEdit = () => {
    setForm(record ? {
      name: record.name || '', phone: record.phone || '', email: record.email || '',
      date_of_birth: record.date_of_birth || '', gender: record.gender || '', address: record.address || '',
      allergies: record.allergies || [], notes: record.notes || '', category: record.category || 'cash',
      g6pd_status: record.g6pd_status || '', sickling_status: record.sickling_status || '',
      medical_history: record.medical_history || '', medical_conditions: record.medical_conditions || '',
      current_medications: record.current_medications || '', social_history: record.social_history || '',
      environmental_factors: record.environmental_factors || '', pregnancy_status: record.pregnancy_status || '',
    } : { ...EMPTY, name: seed?.name || '', phone: seed?.phone || '', email: seed?.email || '' });
    setAllergyInput('');
    setEditing(true);
  };

  const addAllergy = () => {
    const v = allergyInput.trim();
    if (v && !form.allergies.includes(v)) { setForm((f) => ({ ...f, allergies: [...f.allergies, v] })); setAllergyInput(''); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (legacyPatientId) {
        await api.put(`/patients/${legacyPatientId}`, form);
        toast.success('Patient record updated');
        await load(legacyPatientId);
      } else {
        const r = await api.post('/patients', form);
        toast.success('Patient record created');
        onCreated(r.data.patient_id);
      }
      setEditing(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this patient record? Portal purchase/chat history is not affected.')) return;
    try { await api.delete(`/patients/${legacyPatientId}`); toast.success('Patient record deleted'); onCreated(null); }
    catch { toast.error('Delete failed'); }
  };

  const addNote = async () => {
    if (!noteBody.trim() || !legacyPatientId) return;
    try { await api.post(`/patients/${legacyPatientId}/notes`, { body: noteBody.trim() }); setNoteBody(''); await load(legacyPatientId); }
    catch { toast.error('Could not add note'); }
  };

  const togglePin = async (noteId, pinned) => {
    try { await api.put(`/patients/${legacyPatientId}/notes/${noteId}/pin`, { pinned: !pinned }); await load(legacyPatientId); }
    catch { toast.error('Could not update note'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground flex items-center gap-1" data-testid="care-record-loading"><Loader2 className="w-4 h-4 animate-spin" />Loading patient record…</p>;

  if (!legacyPatientId && !editing) {
    return <div className="care-record space-y-2" data-testid="care-record-empty">
      <p className="text-sm">No walk-in patient record yet for this person.</p>
      <Button size="sm" onClick={startEdit} data-testid="care-record-create-btn"><Plus className="w-4 h-4 mr-1" />Create patient record</Button>
    </div>;
  }

  if (editing) {
    return <div className="space-y-3" data-testid="care-record-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="care-record-name" /></div>
        <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="care-record-phone" /></div>
        <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="care-record-email" /></div>
        <div><Label>Date of birth</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="care-record-dob" /></div>
        <div><Label>Gender</Label><Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="care-record-gender" /></div>
        <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="care-record-address" /></div>
        <div>
          <Label>Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="care-record-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="cash">Cash / Walk-in</SelectItem><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="corporate">Corporate</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>G6PD status</Label><Input value={form.g6pd_status} onChange={(e) => setForm({ ...form, g6pd_status: e.target.value })} data-testid="care-record-g6pd" /></div>
        <div><Label>Sickling status</Label><Input value={form.sickling_status} onChange={(e) => setForm({ ...form, sickling_status: e.target.value })} data-testid="care-record-sickling" /></div>
        <div><Label>Pregnancy status</Label><Input value={form.pregnancy_status} onChange={(e) => setForm({ ...form, pregnancy_status: e.target.value })} data-testid="care-record-pregnancy" /></div>
      </div>
      <div className="space-y-2">
        <Label>Allergies</Label>
        <div className="flex flex-wrap gap-1.5">{form.allergies.map((a, i) => <span key={a} className="inline-flex items-center gap-1 bg-red-50 text-red-700 rounded-full px-2.5 py-1 text-sm">{a}<button type="button" onClick={() => setForm((f) => ({ ...f, allergies: f.allergies.filter((_, j) => j !== i) }))}><X className="w-3 h-3" /></button></span>)}</div>
        <div className="flex gap-2">
          <Input value={allergyInput} onChange={(e) => setAllergyInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergy(); } }} placeholder="Type and press Enter" data-testid="care-record-allergy-input" />
          <Button type="button" variant="outline" onClick={addAllergy}><Plus className="w-4 h-4" /></Button>
        </div>
      </div>
      <div><Label>Medical conditions</Label><Textarea rows={2} value={form.medical_conditions} onChange={(e) => setForm({ ...form, medical_conditions: e.target.value })} data-testid="care-record-conditions" /></div>
      <div><Label>Current medications</Label><Textarea rows={2} value={form.current_medications} onChange={(e) => setForm({ ...form, current_medications: e.target.value })} data-testid="care-record-medications" /></div>
      <div><Label>Medical history</Label><Textarea rows={2} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} data-testid="care-record-history" /></div>
      <div><Label>Social / environmental history</Label><Textarea rows={2} value={form.social_history} onChange={(e) => setForm({ ...form, social_history: e.target.value })} data-testid="care-record-social" /></div>
      <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="care-record-notes-field" /></div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="care-record-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
        <Button variant="outline" onClick={() => setEditing(false)} data-testid="care-record-cancel">Cancel</Button>
      </div>
    </div>;
  }

  return <div className="space-y-4" data-testid="care-record-view">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="care-status-grid">
        <span>DOB: <b data-testid="care-record-view-dob">{record.date_of_birth || '—'}</b></span>
        <span>Gender: <b>{record.gender || '—'}</b></span>
        <span>Category: <b>{record.category || 'cash'}</b></span>
        <span>G6PD: <b data-testid="care-record-view-g6pd">{record.g6pd_status || '—'}</b></span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={startEdit} data-testid="care-record-edit-btn"><Pencil className="w-4 h-4 mr-1" />Edit</Button>
        {isAdmin && <Button size="sm" variant="outline" className="text-red-600" onClick={remove} data-testid="care-record-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>}
      </div>
    </div>
    <div><p className="text-xs font-semibold uppercase text-muted-foreground">Allergies</p><p className="text-sm" data-testid="care-record-view-allergies">{(record.allergies || []).length ? record.allergies.join(', ') : 'None recorded'}</p></div>
    {record.medical_conditions && <div><p className="text-xs font-semibold uppercase text-muted-foreground">Medical conditions</p><p className="text-sm whitespace-pre-wrap">{record.medical_conditions}</p></div>}
    {record.current_medications && <div><p className="text-xs font-semibold uppercase text-muted-foreground">Current medications</p><p className="text-sm whitespace-pre-wrap">{record.current_medications}</p></div>}
    {record.notes && <div><p className="text-xs font-semibold uppercase text-muted-foreground">Notes</p><p className="text-sm whitespace-pre-wrap" data-testid="care-record-view-notes">{record.notes}</p></div>}

    {isPharmacist && aiNotes.length > 0 && (
      <div data-testid="care-ai-notes-section">
        <p className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
          <FlaskConical className="w-3.5 h-3.5" />AI Clinical Notes ({aiNotes.length})
        </p>
        <div className="space-y-2">
          {aiNotes.map((n) => (
            <div key={n.note_id} className="rounded-lg border p-3 space-y-1.5 bg-muted/20" data-testid={`care-ai-note-${n.note_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <span className="text-xs font-medium text-foreground line-clamp-2">{n.question}</span>
                {n.status === 'reviewed' ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold shrink-0" data-testid={`care-ai-note-status-${n.note_id}`}>
                    <CheckCircle className="w-3 h-3" />Reviewed
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-semibold shrink-0" data-testid={`care-ai-note-status-${n.note_id}`}>
                    <Clock className="w-3 h-3" />AI · Pending Review
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">{n.answer.replace(/#{1,6}\s/g, '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/`(.+?)`/g, '$1')}</p>
              {n.drugs?.length > 0 && (
                <p className="text-[10px] text-muted-foreground">Drugs: {n.drugs.join(', ')}</p>
              )}
              <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground flex-wrap">
                <span>By {n.created_by_name} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                <div className="flex items-center gap-1.5">
                  {n.status === 'pending_review' && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                      disabled={reviewingId === n.note_id}
                      onClick={() => markReviewed(n.note_id)}
                      data-testid={`care-ai-note-review-btn-${n.note_id}`}>
                      {reviewingId === n.note_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                      Mark reviewed
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                    onClick={() => downloadPdf(n.note_id)}
                    data-testid={`care-ai-note-pdf-btn-${n.note_id}`}
                    title="Download PDF">
                    <Download className="w-3 h-3 mr-1" />PDF
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                    onClick={() => shareWhatsApp(n)}
                    data-testid={`care-ai-note-wa-btn-${n.note_id}`}
                    title="Share via WhatsApp">
                    <MessageCircle className="w-3 h-3 mr-1" />WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="flex gap-2 items-start"><Textarea rows={2} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add a clinical note…" data-testid="care-record-note-input" /><Button onClick={addNote} disabled={!noteBody.trim()} data-testid="care-record-note-add">Add</Button></div>

    <div>
      <h3 className="text-sm font-semibold mb-2">Timeline</h3>
      {!timeline?.events?.length && <p className="care-empty" data-testid="care-record-timeline-empty">No recorded activity yet.</p>}
      <div className="space-y-2">
        {(timeline?.events || []).map((ev) => {
          const Icon = EVENT_ICON[ev.type] || StickyNote;
          return <div key={`${ev.type}-${ev.ref}`} className="care-record flex items-start gap-2" data-testid={`care-record-event-${ev.ref}`}>
            <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium">{ev.title}</span><span className="text-xs text-muted-foreground">{ev.date ? new Date(ev.date).toLocaleString() : ''}</span></div>
              <p className="text-xs text-muted-foreground break-words">{ev.summary}{ev.amount ? ` · GHS ${Number(ev.amount).toFixed(2)}` : ''}{ev.type === 'counseling_slip' && ev.meta?.sent_by_name ? ` · by ${ev.meta.sent_by_name}` : ''}</p>
              {ev.type === 'note' && <button type="button" className="text-xs underline mt-1" onClick={() => togglePin(ev.ref, ev.meta?.pinned)} data-testid={`care-record-note-pin-${ev.ref}`}>{ev.meta?.pinned ? 'Unpin' : 'Pin'}</button>}
            </div>
          </div>;
        })}
      </div>
    </div>
  </div>;
};
